import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, makeStyles } from '@material-ui/core'
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp'
import EditIcon from '@material-ui/icons/Edit'
import CheckIcon from '@material-ui/icons/Check'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import SydButton from '../../../../../../commonDesign/Button'
import PurposeDetailPanel from '../../components/configurationManagement/PurposeDetailPanel'

const MONO = "'SF Mono', 'Fira Code', Menlo, Consolas, monospace"

const useStyles = makeStyles(() => ({
  card: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white',
    overflow: 'hidden',
    transition: 'border-color 0.15s ease',
    '&:hover': {
      borderColor: 'var(--color-neutral-300)'
    }
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-050)'
    }
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    minWidth: 0
  },
  title: {
    fontSize: '13.5px',
    fontWeight: 600,
    color: 'var(--color-neutral-900)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  code: {
    fontFamily: MONO,
    fontSize: '11px',
    color: 'var(--color-neutral-500)',
    flexShrink: 0
  },
  subtitle: {
    fontFamily: MONO,
    fontSize: '11px',
    color: 'var(--color-neutral-500)'
  },
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap'
  },
  chip: {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    padding: '2px 7px',
    borderRadius: '3px',
    fontFamily: MONO,
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-600)'
  },
  chipPrimary: {
    backgroundColor: 'var(--color-primary-050)',
    color: 'var(--color-primary-700)'
  },
  chipAi: {
    backgroundColor: 'var(--color-ai-100)',
    color: 'var(--color-ai-700)'
  },
  chipWarn: {
    backgroundColor: 'var(--color-warning-050, #fffbeb)',
    color: 'var(--color-warning-700, #b45309)'
  },
  expandIcon: {
    color: 'var(--color-neutral-400)',
    flexShrink: 0,
    fontSize: '20px'
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0
  },
  iconButton: {
    padding: '4px'
  },
  iconButtonActive: {
    color: 'var(--color-success-600)',
    backgroundColor: 'var(--color-success-050, #f0fdf4)'
  },
  iconButtonDanger: {
    color: 'var(--color-danger-500, #ef4444)',
    '&:hover': {
      backgroundColor: 'var(--color-danger-050, #fef2f2)'
    }
  },
  editingCard: {
    borderColor: 'var(--color-primary-300)',
    boxShadow: '0 0 0 3px var(--color-primary-050)'
  },
  body: {
    padding: '0 14px 14px',
    borderTop: '1px solid var(--color-neutral-100)'
  },
  quickActionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0 4px',
    flexWrap: 'wrap'
  },
  editBody: {
    padding: '12px 14px 14px',
    borderTop: '1px solid var(--color-primary-200)',
    backgroundColor: 'var(--color-primary-050, #f0f7ff)'
  },
  section: {
    marginTop: '12px'
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--color-neutral-500)',
    marginBottom: '6px',
    fontFamily: MONO
  },
  dqlBlock: {
    fontFamily: MONO,
    fontSize: '11.5px',
    lineHeight: 1.6,
    backgroundColor: 'var(--color-neutral-050)',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '4px',
    padding: '10px 12px',
    overflow: 'auto',
    whiteSpace: 'pre',
    color: 'var(--color-neutral-800)',
    margin: 0,
    maxHeight: '240px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px'
  },
  th: {
    textAlign: 'left',
    padding: '6px 8px',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-500)',
    borderBottom: '1px solid var(--color-neutral-200)',
    fontFamily: MONO
  },
  td: {
    padding: '6px 8px',
    borderBottom: '1px solid var(--color-neutral-100)',
    color: 'var(--color-neutral-800)'
  },
  tdMono: {
    fontFamily: MONO,
    fontSize: '11.5px'
  },
  flagYes: { color: 'var(--color-success-600)', fontWeight: 600 },
  flagNo: { color: 'var(--color-neutral-300)' },
  predicateBlock: {
    fontFamily: MONO,
    fontSize: '11px',
    backgroundColor: 'var(--color-neutral-050)',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '4px',
    padding: '8px 10px',
    color: 'var(--color-neutral-700)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '4px 14px',
    fontSize: '12px'
  },
  detailLabel: {
    fontFamily: MONO,
    fontSize: '11px',
    color: 'var(--color-neutral-500)'
  },
  detailValue: {
    color: 'var(--color-neutral-800)'
  },
  detailValueMono: {
    fontFamily: MONO,
    fontSize: '11.5px',
    color: 'var(--color-neutral-800)'
  }
}))

function tableSummary (tables) {
  if (!Array.isArray(tables) || tables.length === 0) return '(no tables)'
  return tables.map(t => {
    const full = t.schema ? `${t.schema}.${t.tableCode}` : t.tableCode
    return t.alias ? `${full} ${t.alias}` : full
  }).join(', ')
}

function PurposeCard ({ purposeCode, purpose, defaultExpanded = false, onUpdatePurpose, onDeletePurpose, onAnalyzeDql }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [editing, setEditing] = useState(false)

  const columns = Array.isArray(purpose.columns) ? purpose.columns : []
  const tables = Array.isArray(purpose.tables) ? purpose.tables : []
  const subordinate = Array.isArray(purpose.subordinatePredicates) ? purpose.subordinatePredicates : []
  const scope = Array.isArray(purpose.scopePredicates) ? purpose.scopePredicates : []
  const masterExpr = (purpose.masterPredicate?.expression || '').trim()
  const masterParams = purpose.masterPredicate?.requiredParams || []
  const latch = purpose.latch
  const pagination = purpose.pagination

  const handleHeaderClick = () => {
    if (editing) return
    setExpanded(e => !e)
  }

  const handleToggleEdit = (e) => {
    e.stopPropagation()
    if (!editing) {
      setExpanded(true)
      setEditing(true)
    } else {
      setEditing(false)
    }
  }

  const handleDelete = (e) => {
    e.stopPropagation()
    if (!onDeletePurpose) return
    const ok = window.confirm(
      `Delete purpose "${purpose.displayName || purposeCode}"? This will also break any presentation options that reference it.`
    )
    if (ok) onDeletePurpose(purposeCode)
  }

  const handleOpenInQueryTool = useCallback(() => {
    if (!purpose?.sourceQuery) return
    localStorage.setItem('denaliQueryToolDql', purpose.sourceQuery)
    window.open('/admin/denali/analysis/catalog/query?fromAssistant=1', '_blank')
  }, [purpose])

  return (
    <div className={`${classes.card} ${editing ? classes.editingCard : ''}`}>
      <div
        className={classes.cardHeader}
        onClick={handleHeaderClick}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleHeaderClick() } }}
      >
        <div className={classes.titleBlock}>
          <div className={classes.titleRow}>
            <span className={classes.title}>{purpose.displayName || purposeCode}</span>
            <span className={classes.code}>{purposeCode}</span>
          </div>
          <div className={classes.subtitle}>
            {tableSummary(tables)}
          </div>
          <div className={classes.chipRow} style={{ marginTop: '4px' }}>
            <span className={classes.chip}>{columns.length} col{columns.length === 1 ? '' : 's'}</span>
            {latch && <span className={`${classes.chip} ${classes.chipPrimary}`}>latch</span>}
            {masterExpr && <span className={`${classes.chip} ${classes.chipAi}`}>master predicate</span>}
            {subordinate.length > 0 && (
              <span className={classes.chip}>
                {subordinate.length} subordinate
              </span>
            )}
            {scope.length > 0 && (
              <span className={classes.chip}>
                {scope.length} scope
              </span>
            )}
            {masterParams.length > 0 && (
              <span className={`${classes.chip} ${classes.chipWarn}`}>
                params: {masterParams.join(', ')}
              </span>
            )}
          </div>
        </div>
        <div className={classes.headerButtons}>
          {onUpdatePurpose && (
            <IconButton
              size='small'
              className={`${classes.iconButton} ${editing ? classes.iconButtonActive : ''}`}
              onClick={handleToggleEdit}
              title={editing ? 'Done editing' : 'Edit purpose'}
              aria-label={editing ? 'Done editing' : 'Edit purpose'}
            >
              {editing ? <CheckIcon fontSize='small' /> : <EditIcon fontSize='small' />}
            </IconButton>
          )}
          {onDeletePurpose && !editing && (
            <IconButton
              size='small'
              className={`${classes.iconButton} ${classes.iconButtonDanger}`}
              onClick={handleDelete}
              title='Delete purpose'
              aria-label='Delete purpose'
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          )}
          {!editing && (expanded
            ? <KeyboardArrowUp className={classes.expandIcon} />
            : <KeyboardArrowDown className={classes.expandIcon} />)}
        </div>
      </div>

      {editing && onUpdatePurpose && (
        <div className={classes.editBody}>
          <PurposeDetailPanel
            purposeCode={purposeCode}
            purposeData={purpose}
            onUpdatePurpose={onUpdatePurpose}
            onAnalyzeDql={onAnalyzeDql}
          />
        </div>
      )}

      {expanded && !editing && (
        <div className={classes.body}>
          <div className={classes.quickActionsRow}>
            <SydButton
              size='xs'
              variant='outline'
              icon='openInNew'
              onClick={handleOpenInQueryTool}
              disabled={!purpose.sourceQuery}
            >
              Open in Query Tool
            </SydButton>
            {onAnalyzeDql && (
              <SydButton
                size='xs'
                variant='outline'
                icon='queryStats'
                onClick={() => onAnalyzeDql(purposeCode, purpose.sourceQuery || '')}
                title='Run DQL diagnostics on this purpose without leaving the studio.'
              >
                Diagnose
              </SydButton>
            )}
          </div>
          {purpose.sourceQuery && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>DQL</div>
              <pre className={classes.dqlBlock}>{purpose.sourceQuery}</pre>
            </div>
          )}

          {columns.length > 0 && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>Columns ({columns.length})</div>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>Name</th>
                    <th className={classes.th}>Type</th>
                    <th className={classes.th}>Source</th>
                    <th className={classes.th} style={{ textAlign: 'center' }}>Filter</th>
                    <th className={classes.th} style={{ textAlign: 'center' }}>Sort</th>
                    <th className={classes.th} style={{ textAlign: 'center' }}>Faceted</th>
                    <th className={classes.th} style={{ textAlign: 'center' }}>Hidden</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map((col, i) => (
                    <tr key={col.name || i}>
                      <td className={`${classes.td} ${classes.tdMono}`}>{col.name}</td>
                      <td className={classes.td}>{col.dataType}</td>
                      <td className={`${classes.td} ${classes.tdMono}`} style={{ color: 'var(--color-neutral-500)' }}>
                        {col.sourceExpression}
                      </td>
                      <td className={classes.td} style={{ textAlign: 'center' }}>
                        <span className={col.filterable ? classes.flagYes : classes.flagNo}>
                          {col.filterable ? '✓' : '·'}
                        </span>
                      </td>
                      <td className={classes.td} style={{ textAlign: 'center' }}>
                        <span className={col.sortable ? classes.flagYes : classes.flagNo}>
                          {col.sortable ? '✓' : '·'}
                        </span>
                      </td>
                      <td className={classes.td} style={{ textAlign: 'center' }}>
                        <span className={col.faceted ? classes.flagYes : classes.flagNo}>
                          {col.faceted ? '✓' : '·'}
                        </span>
                      </td>
                      <td className={classes.td} style={{ textAlign: 'center' }}>
                        <span className={col.hidden ? classes.flagYes : classes.flagNo}>
                          {col.hidden ? '✓' : '·'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {latch && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>Latch</div>
              <div className={classes.detailGrid}>
                <span className={classes.detailLabel}>parameter</span>
                <span className={classes.detailValueMono}>:{latch.parameterName}</span>
                <span className={classes.detailLabel}>date column</span>
                <span className={classes.detailValueMono}>{latch.dateColumn}</span>
                <span className={classes.detailLabel}>date table</span>
                <span className={classes.detailValueMono}>{latch.dateTable}</span>
                <span className={classes.detailLabel}>default</span>
                <span className={classes.detailValue}>{latch.defaultBehavior || 'latest'}</span>
                {latch.limit && (
                  <>
                    <span className={classes.detailLabel}>limit</span>
                    <span className={classes.detailValue}>{latch.limit}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {masterExpr && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>Master predicate</div>
              <pre className={classes.predicateBlock}>{masterExpr}</pre>
            </div>
          )}

          {subordinate.length > 0 && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>Subordinate predicates ({subordinate.length})</div>
              {subordinate.map((p, i) => (
                <div key={p.code || i} style={{ marginBottom: '8px' }}>
                  <div className={classes.detailValueMono} style={{ marginBottom: '2px' }}>
                    {p.code}{p.parameterized ? ' (parameterized)' : ''}
                  </div>
                  <pre className={classes.predicateBlock}>{p.expression}</pre>
                </div>
              ))}
            </div>
          )}

          {scope.length > 0 && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>Scope predicates ({scope.length})</div>
              {scope.map((s, i) => (
                <div key={s.code || i} className={classes.detailGrid} style={{ marginBottom: '8px' }}>
                  <span className={classes.detailLabel}>{s.code}</span>
                  <span className={classes.detailValue}>
                    {s.scopeType} · {s.type || 'membership'}
                    {s.column ? ` · column: ${s.column}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}

          {pagination && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>Pagination</div>
              <div className={classes.detailGrid}>
                <span className={classes.detailLabel}>default page</span>
                <span className={classes.detailValue}>{pagination.defaultPageSize ?? 100} rows</span>
                <span className={classes.detailLabel}>max page</span>
                <span className={classes.detailValue}>{pagination.maxPageSize ?? 1000} rows</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

PurposeCard.propTypes = {
  purposeCode: PropTypes.string.isRequired,
  purpose: PropTypes.object.isRequired,
  defaultExpanded: PropTypes.bool,
  onUpdatePurpose: PropTypes.func,
  onDeletePurpose: PropTypes.func,
  onAnalyzeDql: PropTypes.func
}

export default PurposeCard
